import React from "react";
import { BRAIN_TYPE_LABELS } from "./BrainTypeProbabilities";

/**
 * Build the canonical brain-type label used everywhere in the app
 * (e.g. "#9 AFCL (Mediator)"). Mirrors the backend's auto-assignment in
 * /participants/{id}/workshop-result so consultants get the same format
 * whether the brain type was set manually or computed from a workshop.
 */
export function formatBrainTypeFull(id) {
  const m = BRAIN_TYPE_LABELS.find((x) => x.id === Number(id));
  if (!m) return "";
  return `#${m.id} ${m.code} (${m.informal})`;
}

/**
 * Parse an existing free-form participant.brain_type string back to the
 * matching brain-type id (1..9) — works with the canonical format as well as
 * legacy values like "#9", "9", "AFCL" or "Mediator".
 */
export function parseBrainTypeValue(value) {
  if (!value) return "";
  const s = String(value).trim();
  // "#9 ..." or "9 ..."
  const m = s.match(/#?\s*([1-9])\b/);
  if (m) return m[1];
  // by 4-letter code
  const codeMatch = BRAIN_TYPE_LABELS.find((x) => s.toUpperCase().includes(x.code));
  if (codeMatch) return String(codeMatch.id);
  // by informal name
  const informalMatch = BRAIN_TYPE_LABELS.find((x) => s.toLowerCase().includes(x.informal.toLowerCase()));
  if (informalMatch) return String(informalMatch.id);
  return "";
}

/**
 * Dropdown selector that maps a brain-type id (1..9, or empty string for
 * "not yet determined") to / from the canonical full label.
 *
 * Props:
 *   value:    string ("" or canonical full label)
 *   onChange: receives the full label (or "")
 *   testid:   data-testid for the <select>
 */
export default function BrainTypeSelect({ value, onChange, testid = "brain-type-select", label = "Brain type" }) {
  const currentId = parseBrainTypeValue(value);
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</label>
      <select
        value={currentId}
        onChange={(e) => {
          const id = e.target.value;
          onChange(id ? formatBrainTypeFull(id) : "");
        }}
        data-testid={testid}
        className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white"
      >
        <option value="">— Nog niet bepaald —</option>
        {BRAIN_TYPE_LABELS.map((m) => (
          <option key={m.id} value={m.id}>
            #{m.id} {m.code} ({m.informal})
          </option>
        ))}
      </select>
    </div>
  );
}
